import { delay, HttpResponse, http } from "msw"
import {
	getAcceptMarketingTermsMockHandler,
	getGetMarketingContextMockHandler,
	getGetMarketingTimezonesMockHandler,
} from "@/shared/api/generated/marketing-context/marketing-context.msw"
import { getGetMarketingProvidersMockHandler } from "@/shared/api/generated/marketing-providers/marketing-providers.msw"
import { getGetMarketingRunsSummaryMockHandler } from "@/shared/api/generated/marketing-runs/marketing-runs.msw"
import { getGetMarketingFilterMetadataMockHandler } from "@/shared/api/generated/marketing-segments/marketing-segments.msw"
import {
	getCreateMarketingTaskMockHandler,
	getListMarketingTasksMockHandler,
	getRunMarketingTaskNowMockHandler,
} from "@/shared/api/generated/marketing-tasks/marketing-tasks.msw"
import type {
	MarketingTaskType,
	MarketingTaskUpsertRequest,
	MessagePreviewRequest,
	SaveSegmentRequest,
	SegmentPreviewRequest,
	ToggleMarketingTaskRequest,
	UpdateSavedSegmentRequest,
} from "@/shared/api/generated/types/marketing"

import {
	getMarketingTaskListFixture,
	marketingContextFixture,
	marketingProvidersFixture,
	marketingRunsListFixture,
	marketingRunsSummaryFixture,
	marketingTimezonesFixture,
} from "./marketing-fixtures"
import { saveMarketingMedia } from "./marketing-media-store"
import { computeMessagePreviewFixture } from "./marketing-message-fixtures"
import { getMessageVariablesFixture } from "./marketing-message-variables-fixtures"
import { marketingReportFieldsFixture } from "./marketing-report-fields-fixtures"
import {
	getMarketingRunDetailFixture,
	listMarketingRunRecipientsFixture,
} from "./marketing-run-fixtures"
import {
	createSavedSegment,
	deleteSavedSegment,
	listSavedSegments,
	updateSavedSegment,
} from "./marketing-saved-segments-store"
import {
	computeSegmentPreviewFixture,
	marketingFilterMetadataFixture,
} from "./marketing-segment-fixtures"
import {
	createMarketingTaskInStore,
	deleteMarketingTaskInStore,
	duplicateMarketingTaskInStore,
	getMarketingTaskById,
	toggleMarketingTaskInStore,
	updateMarketingTaskInStore,
} from "./marketing-task-store"

const notFoundBody = { message: "Not found" }

/**
 * Stable MSW handlers for Marketing PRM UI development.
 * Registered first in `browser.ts` — MSW uses the first matching handler.
 */
export const getMarketingFixtureHandlers = () => [
	getGetMarketingContextMockHandler(marketingContextFixture),
	getAcceptMarketingTermsMockHandler({
		...marketingContextFixture,
		marketingAccepted: true,
	}),
	getGetMarketingTimezonesMockHandler(marketingTimezonesFixture),
	getGetMarketingFilterMetadataMockHandler(marketingFilterMetadataFixture),
	http.get("*/marketing/report-fields", async () => {
		await delay(150)
		return HttpResponse.json(marketingReportFieldsFixture, { status: 200 })
	}),
	http.get("*/marketing/segments/saved", async ({ request }) => {
		await delay(200)
		const url = new URL(request.url)
		const businessId = url.searchParams.get("businessId") ?? ""
		return HttpResponse.json(listSavedSegments(businessId), { status: 200 })
	}),
	http.post("*/marketing/segments/saved", async ({ request }) => {
		await delay(250)
		const body = (await request.json()) as SaveSegmentRequest
		const segment = createSavedSegment(body)
		return HttpResponse.json(segment, { status: 201 })
	}),
	http.put(
		"*/marketing/segments/saved/:segmentId",
		async ({ params, request }) => {
			await delay(250)
			const url = new URL(request.url)
			const businessId = url.searchParams.get("businessId") ?? ""
			const segmentId = params.segmentId as string
			const body = (await request.json()) as UpdateSavedSegmentRequest
			const segment = updateSavedSegment(segmentId, businessId, body)
			if (!segment) {
				return HttpResponse.json(notFoundBody, { status: 404 })
			}
			return HttpResponse.json(segment, { status: 200 })
		},
	),
	http.delete(
		"*/marketing/segments/saved/:segmentId",
		async ({ params, request }) => {
			await delay(200)
			const url = new URL(request.url)
			const businessId = url.searchParams.get("businessId") ?? ""
			const segmentId = params.segmentId as string
			const deleted = deleteSavedSegment(segmentId, businessId)
			if (!deleted) {
				return HttpResponse.json(notFoundBody, { status: 404 })
			}
			return new HttpResponse(null, { status: 204 })
		},
	),
	http.post("*/marketing/segments/preview", async ({ request }) => {
		await delay(350)
		const body = (await request.json()) as SegmentPreviewRequest
		return HttpResponse.json(computeSegmentPreviewFixture(body), {
			status: 200,
		})
	}),
	getGetMarketingProvidersMockHandler(marketingProvidersFixture),
	http.get("*/marketing/messages/variables", async () => {
		await delay(150)
		return HttpResponse.json(getMessageVariablesFixture(), {
			status: 200,
		})
	}),
	http.post("*/marketing/messages/preview", async ({ request }) => {
		await delay(200)
		const body = (await request.json()) as MessagePreviewRequest
		return HttpResponse.json(computeMessagePreviewFixture(body), {
			status: 200,
		})
	}),
	http.post("*/marketing/media", async () => {
		await delay(250)
		const mediaId = `media-${Date.now()}`
		const url = "https://picsum.photos/seed/medme-mailing/320/180"
		saveMarketingMedia(mediaId, url)
		return HttpResponse.json({ mediaId, url }, { status: 201 })
	}),
	getListMarketingTasksMockHandler((info) => {
		const url = new URL(info.request.url)
		const type = url.searchParams.get("type") as MarketingTaskType | null
		const scheduleFilter = url.searchParams.get("scheduleFilter") as
			| "all"
			| "once"
			| "recurring"
			| null
		const page = Number(url.searchParams.get("page") ?? 1)
		const pageSize = Number(url.searchParams.get("pageSize") ?? 20)

		return getMarketingTaskListFixture(type, scheduleFilter, page, pageSize)
	}),
	http.get("*/marketing/tasks/:taskId", async ({ params }) => {
		await delay(300)

		const taskId = params.taskId as string
		const task = getMarketingTaskById(taskId)
		if (!task) {
			return HttpResponse.json(notFoundBody, { status: 404 })
		}

		return HttpResponse.json(task, { status: 200 })
	}),
	getCreateMarketingTaskMockHandler(async (info) => {
		const body = (await info.request.json()) as MarketingTaskUpsertRequest
		return createMarketingTaskInStore(body)
	}),
	http.put("*/marketing/tasks/:taskId", async ({ params, request }) => {
		await delay(300)

		const taskId = params.taskId as string
		const body = (await request.json()) as MarketingTaskUpsertRequest
		const task = updateMarketingTaskInStore(taskId, body)
		if (!task) {
			return HttpResponse.json(notFoundBody, { status: 404 })
		}

		return HttpResponse.json(task, { status: 200 })
	}),
	http.post("*/marketing/tasks/:taskId/duplicate", async ({ params }) => {
		await delay(300)

		const taskId = params.taskId as string
		const task = duplicateMarketingTaskInStore(taskId)
		if (!task) {
			return HttpResponse.json(notFoundBody, { status: 404 })
		}

		return HttpResponse.json(task, { status: 201 })
	}),
	http.delete("*/marketing/tasks/:taskId", async ({ params }) => {
		await delay(300)

		const taskId = params.taskId as string
		const deleted = deleteMarketingTaskInStore(taskId)
		if (!deleted) {
			return HttpResponse.json(notFoundBody, { status: 404 })
		}

		return new HttpResponse(null, { status: 204 })
	}),
	http.post("*/marketing/tasks/:taskId/toggle", async ({ params, request }) => {
		await delay(300)

		const taskId = params.taskId as string
		const body = (await request.json()) as ToggleMarketingTaskRequest
		const task = toggleMarketingTaskInStore(taskId, body.active)
		if (!task) {
			return HttpResponse.json(notFoundBody, { status: 404 })
		}

		return HttpResponse.json(task, { status: 200 })
	}),
	getRunMarketingTaskNowMockHandler(() => ({
		runId: "run-manual-fixture",
		status: "queued" as const,
	})),
	getGetMarketingRunsSummaryMockHandler(marketingRunsSummaryFixture),
	http.get(
		"*/marketing/runs/:runId/recipients",
		async ({ params, request }) => {
			await delay(150)
			const runId = params.runId as string
			const url = new URL(request.url)
			const phone = url.searchParams.get("phone")?.trim()
			const page = Number(url.searchParams.get("page") ?? 1)
			const pageSize = Number(url.searchParams.get("pageSize") ?? 20)

			return HttpResponse.json(
				listMarketingRunRecipientsFixture(runId, {
					phone: phone || undefined,
					page,
					pageSize,
				}),
				{ status: 200 },
			)
		},
	),
	http.get("*/marketing/runs/:runId", async ({ params }) => {
		await delay(150)
		const runId = params.runId as string
		const run = getMarketingRunDetailFixture(runId)

		if (!run) {
			return HttpResponse.json({ message: "Not found" }, { status: 404 })
		}

		return HttpResponse.json(run, { status: 200 })
	}),
	http.post("*/marketing/runs/:runId/resend", async () => {
		await delay(150)
		return HttpResponse.json(
			{ runId: "run-mailing-001", status: "queued" },
			{ status: 202 },
		)
	}),
	http.post(
		"*/marketing/runs/:runId/recipients/:recipientId/resend",
		async ({ params }) => {
			await delay(150)
			return HttpResponse.json(
				{
					runId: params.runId as string,
					status: "queued",
				},
				{ status: 202 },
			)
		},
	),
	http.get("*/marketing/runs/:runId/download", async () => {
		await delay(150)
		return new HttpResponse(new Blob(["fixture"]), {
			status: 200,
			headers: {
				"Content-Type":
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			},
		})
	}),
	http.get("*/marketing/runs", async ({ request }) => {
		await delay(200)
		const url = new URL(request.url)
		const type = url.searchParams.get("type")
		const phone = url.searchParams.get("phone")?.trim()
		const page = Number(url.searchParams.get("page") ?? 1)
		const pageSize = Number(url.searchParams.get("pageSize") ?? 20)

		let data = marketingRunsListFixture.data
		if (type === "mailing" || type === "report") {
			data = data.filter((run) => run.taskType === type)
		}
		if (phone) {
			data = data.filter((run) => run.id.includes(phone))
		}

		const total = data.length
		const start = (page - 1) * pageSize

		return HttpResponse.json(
			{
				data: data.slice(start, start + pageSize),
				paging: { page, pageSize, total },
			},
			{ status: 200 },
		)
	}),
]
